import React, { useContext } from "react";
import { Link } from "@reach/router";
import { useQuery } from "@apollo/react-hooks";
import Header from "../../../components/Header";
import BackLink from "../../../components/BackLink";
import Tabs from "../../../components/Tabs";
import Tab from "../../../components/Tab";
import { AuthContext } from "../../../services/user/AuthProvider";
import { GET_FRANCHISE_SCHOOL } from "../services/graphql";
import styles from "../Franchises.module.scss";

const FranchiseSchool = ({ schoolId, franchiseId, portal = false }) => {
  const { currentUser } = useContext(AuthContext);
  const { data, loading, error } = useQuery(GET_FRANCHISE_SCHOOL, {
    variables: { schoolId },
    fetchPolicy: "no-cache"
  });
  const school = data && data.school;
  const activities = (school && school.franchiseActivities) || [];
  const schoolsPath = portal ? `/franchise-portal/${franchiseId}/schools` : `/franchises/${franchiseId}/schools`;
  const belongsToFranchise = school && school.franchise && String(school.franchise.id) === String(franchiseId);

  return (
    <div className={`${styles.franchises} ${styles.franchiseSchool} ${portal ? styles.portal : ""}`}>
      {portal && <Header user={currentUser} portalFranchise={belongsToFranchise ? school.franchise : null} portalFranchiseId={franchiseId} portalPage="schools" />}
      <BackLink text="Schools" to={schoolsPath} />
      {loading && <p role="status">Loading school...</p>}
      {error && <p role="alert">Unable to load school. Please try again later.</p>}
      {!loading && !error && !belongsToFranchise && <p>School not found in this franchise.</p>}
      {!loading && !error && belongsToFranchise && <>
        <div className={styles.detailHeader}>
          <div>
            <h1 className="title">{school.name}</h1>
            <p className={styles.schoolCode}>School code: {school.schoolCode || "—"}</p>
          </div>
          <div className={styles.schoolHeaderActions}>
            <span className={`${styles.detailStatus} ${styles.schoolStatus}`} data-status={(school.onboardingStatusLabel || "").toLowerCase()}>{school.onboardingStatusLabel || "—"}</span>
            <Link to={`${schoolsPath}/${schoolId}/edit`} className={styles.button}>Edit school</Link>
          </div>
        </div>

        {/* <Tabs className={styles.schoolTabs}>
          <Tab selected>Overview</Tab>
          <Tab disabled>Admin users</Tab>
          <Tab disabled>Subscription</Tab>
          <Tab disabled>Activity</Tab>
        </Tabs> */}

        <div className={styles.section}>
          <h2>Usage statistics</h2>
          <div className={styles.overview}>
            <div className={styles.overviewCard}>
              <h3>Pupil licences</h3>
              <strong>{(school.annualPupilLicences || 0).toLocaleString("en-GB")}</strong>
              <p className={styles.statDescription}>{school.pupilLicencesAssignedLabel || `${school.assignedPupilLicences || 0} assigned`}</p>
            </div>
            <div className={styles.overviewCard}>
              <h3>Teachers</h3>
              <strong>{(school.totalTeachersCount || 0).toLocaleString("en-GB")}</strong>
              <p className={styles.statDescription}>{school.activeTeachersLabel || `${school.activeTeachersCount || 0} active`}</p>
            </div>
            <div className={styles.overviewCard}>
              <h3>Parents</h3>
              <strong>{(school.totalParentsCount || 0).toLocaleString("en-GB")}</strong>
              <p className={styles.statDescription}>{school.activeParentsLabel || `${school.activeParentsCount || 0} active`}</p>
            </div>
            <div className={styles.overviewCard}>
              <h3>Reading logs</h3>
              <strong>{(school.academicYearReadingLogsCount || 0).toLocaleString("en-GB")}</strong>
              <p className={styles.statDescription}>This academic year</p>
            </div>
          </div>
        </div>

        <div className={`${styles.schoolReviewCards} ${styles.section}`}>
          <div className={styles.detailPanel}>
            <div className={styles.directoryHeading}>
              <h2>School details</h2>
              <Link to={`${schoolsPath}/${schoolId}/edit`} className={styles.button}>Edit</Link>
            </div>
            <dl className={`${styles.schoolReviewDetails} ${styles.schoolOverviewDetails}`}>
              <dt>Address</dt><dd>{school.fullAddress || "—"}</dd>
              <dt>Contact</dt><dd>{school.contactLabel || "—"}</dd>
              <dt>Telephone</dt><dd>{school.schoolPhoneNumber || "—"}</dd>
              <dt>Created</dt><dd>{school.createdDateFormatted || "—"}</dd>
            </dl>
          </div>
          <div className={styles.detailPanel}>
            <div className={styles.directoryHeading}>
              <h2>Primary administrator</h2>
              <Link to={`${schoolsPath}/${schoolId}/admins`} className={styles.button}>Manage</Link>
            </div>
            {school.primaryAdministratorName ? <>
              <div className={styles.primaryAdministrator}>
                <span className={styles.addIcon}>{school.primaryAdministratorInitials || "—"}</span>
                <div>
                  <h3>{school.primaryAdministratorName}</h3>
                  <p>{school.primaryAdministratorEmail || "—"}</p>
                  <p className={styles.statDescription}>Last active: {school.primaryAdministratorLastActiveFormatted || "—"}</p>
                </div>
              </div>
              {school.primaryAdministratorStatusLabel && <p className={styles.schoolFormNotice}>{school.primaryAdministratorStatusLabel}</p>}
            </> : <p>No primary administrator assigned.</p>}
          </div>
        </div>

        <div className={`${styles.detailPanel} ${styles.section}`}>
          <div className={styles.directoryHeading}>
            <h2>Pupil licence period</h2>
            <Link to={`${schoolsPath}/${schoolId}/edit`} className={styles.button}>Update</Link>
          </div>
          <div className={styles.schoolLicenceSummary}>
            <div><h3>Annual pupil licences</h3><strong>{(school.annualPupilLicences || 0).toLocaleString("en-GB")}</strong></div>
            <div><h3>Current period</h3><p>{school.licencePeriodFormatted || "—"}</p></div>
            <div><h3>Assigned</h3><p>{school.assignedPupilLicencesLabel || `${school.assignedPupilLicences || 0} of ${school.annualPupilLicences || 0}`}</p></div>
          </div>
          <div className={styles.licenceProgress} role="progressbar" aria-label="Pupil licences assigned" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, Math.max(0, school.assignedPupilLicencesPercentage || 0))}>
            <div style={{ width: `${Math.min(100, Math.max(0, school.assignedPupilLicencesPercentage || 0))}%` }} />
          </div>
          <p className={styles.statDescription}>Licences are annual from the school’s activation date. Pricing is intentionally excluded from this design stage.</p>
        </div>

        <div className={styles.section}>
          <h2>Recent account activity</h2>
          {activities.length === 0 ? <p>No recent account activity.</p> : (
            <ul className={activities.length > 3 ? `${styles.activityList} ${styles.activityScroll}` : styles.activityList}>
              {activities.map(activity => (
                <li key={activity.id}>
                  <span>{activity.description}</span>
                  <span className={styles.activityDate}>{activity.metaFormatted}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </>}
    </div>
  );
};

export default FranchiseSchool;
